import { Controller } from "@hotwired/stimulus"

// Comments on a document (assistant_documents/_comments). The pane is folded
// until the title menu ("Show all comments", by window event - the menu may sit
// in the side panel's head) or a section's own comment button opens it; adding,
// resolving, reopening and removing are each one request, after which the view
// reloads itself - the panel by asking itself to open the same document, the
// page by reloading - and the pane stays open across that reload.
const OPEN_KEY = "jane-document-comments-open"

export default class extends Controller {
    static targets = ["pane", "section", "body", "error", "quote", "askButton", "commentButton"]
    static values = { url: String, panel: Boolean, panelUrl: String, title: String, key: String, askUrl: String, conversationId: Number, editing: Boolean }

    connect() {
        this._toggle = () => this._setOpen(this.paneTarget.hidden)
        window.addEventListener("document:toggle-comments", this._toggle)
        // The editor's Comment / Ask buttons: open the pane aimed at the selected text.
        this._onSelection = (event) => this.aboutSelection(event.detail || {})
        window.addEventListener("document:comment-on", this._onSelection)
        if (this._wasOpen()) this.paneTarget.hidden = false
    }

    disconnect() {
        window.removeEventListener("document:toggle-comments", this._toggle)
        window.removeEventListener("document:comment-on", this._onSelection)
    }

    // A passage selected in the editor: show it above the box, so the comment (or the ask) says what it is about.
    aboutSelection({ quote, ask }) {
        this._quote = (quote || "").trim()
        this._setOpen(true)
        if (this.hasQuoteTarget) {
            this.quoteTarget.textContent = this._quote.length > 240 ? `${this._quote.slice(0, 240)}…` : this._quote
            this.quoteTarget.hidden = !this._quote
        }
        if (this.hasSectionTarget) this.sectionTarget.value = ""
        if (this.hasBodyTarget) {
            this.bodyTarget.placeholder = ask ? "Ask Jane to change this…" : "Add a comment"
            this.bodyTarget.focus()
        }
        this.paneTarget.scrollIntoView({ block: "nearest", behavior: "smooth" })
    }

    close() {
        this._setOpen(false)
    }

    // A section's comment button: open the pane aimed at that section.
    commentOn(event) {
        const index = event.currentTarget.dataset.sectionIndex
        this._setOpen(true)
        if (this.hasSectionTarget) this.sectionTarget.value = index
        this.hasBodyTarget && this.bodyTarget.focus()
        this.paneTarget.scrollIntoView({ block: "nearest", behavior: "smooth" })
    }

    async submit(event) {
        event.preventDefault()
        await this._post(false)
    }

    // The same comment, and then handed to Jane in the chat the document was drafted in.
    async commentAndAsk() {
        await this._post(true)
    }

    async _post(ask) {
        const body = this.bodyTarget.value.trim()
        if (!body) return this._error("Write a comment first.")

        this._error(null)
        const section = this.sectionTarget.value
        const created = await this._request(this.urlValue, "POST", { body, quote: this._quote || null, section_index: section === "" ? null : Number(section) })
        if (!created) return

        if (ask && created.ask_text) await this._sendToJane(created.ask_text)
        this._quote = null
        this._reload()
    }

    async _sendToJane(text) {
        if (!this.askUrlValue || !this.conversationIdValue) return

        const data = new FormData()
        data.append("message", text)
        data.append("conversation_id", this.conversationIdValue)
        try {
            await fetch(this.askUrlValue, { method: "POST", credentials: "same-origin", body: data, headers: { Accept: "application/json", "X-CSRF-Token": this._csrf() } })
        } catch (_error) { /* the comment is saved; Ask can be pressed again on it */ }
    }

    async resolve(event) { await this._act(event, "PATCH", { resolved: true }) }
    async reopen(event) { await this._act(event, "PATCH", { resolved: false }) }

    async remove(event) {
        if (!window.confirm("Delete this comment?")) return

        await this._act(event, "DELETE")
    }

    // Hand the comment to Jane in the chat the document was drafted in; she
    // reads the document and saves a revision, which shows up here as a new version.
    async askJane(event) {
        const button = event.currentTarget
        if (!this.askUrlValue || !this.conversationIdValue) return

        button.disabled = true
        const data = new FormData()
        data.append("message", button.dataset.askText)
        data.append("conversation_id", this.conversationIdValue)
        try {
            const response = await fetch(this.askUrlValue, {
                method: "POST", credentials: "same-origin", body: data,
                headers: { Accept: "application/json", "X-CSRF-Token": this._csrf() }
            })
            button.textContent = response.ok ? "Asked - see the chat" : "Couldn't ask"
        } catch (_error) {
            button.textContent = "Couldn't ask"
            button.disabled = false
        }
    }

    async _act(event, method, body) {
        const id = event.currentTarget.dataset.commentId
        const ok = await this._request(`${this.urlValue}/${id}`, method, body)
        if (ok) this._reload()
    }

    async _request(url, method, body) {
        try {
            const response = await fetch(url, {
                method, credentials: "same-origin",
                headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": this._csrf() },
                body: body ? JSON.stringify(body) : undefined
            })
            if (response.ok) return (await response.json().catch(() => null)) || true

            const data = await response.json().catch(() => ({}))
            this._error(data.error || "That didn't work. Try again.")
        } catch (_error) {
            this._error("That didn't work. Check your connection and try again.")
        }
        return false
    }

    _reload() {
        this._remember(true)
        // Beside the editor the page cannot reload (what is being typed would be lost): only the comments are fetched again.
        if (this.editingValue) return this._refreshPane()

        if (this.panelValue) {
            window.dispatchEvent(new CustomEvent("jane:open-panel", {
                detail: { url: this.panelUrlValue, title: this.titleValue, icon: "bi-file-earmark-word", label: "Document" }
            }))
        } else {
            window.location.reload()
        }
    }

    async _refreshPane() {
        try {
            const response = await fetch(this.panelValue ? this.panelUrlValue : window.location.href, { credentials: "same-origin", headers: { Accept: "text/html" } })
            const fresh = new DOMParser().parseFromString(await response.text(), "text/html").querySelector("[data-document-comments-target='pane']")
            if (!fresh) return

            this.paneTarget.innerHTML = fresh.innerHTML
            this.paneTarget.hidden = false
        } catch (_error) { /* the next open shows them */ }
    }

    _setOpen(open) {
        this.paneTarget.hidden = !open
        this._remember(open)
    }

    _wasOpen() {
        try { return window.sessionStorage.getItem(`${OPEN_KEY}:${this.keyValue}`) === "1" } catch (_error) { return false }
    }

    _remember(open) {
        try { window.sessionStorage.setItem(`${OPEN_KEY}:${this.keyValue}`, open ? "1" : "0") } catch (_error) { /* no storage: it just starts folded */ }
    }

    _error(message) {
        if (!this.hasErrorTarget) return

        this.errorTarget.textContent = message || ""
        this.errorTarget.hidden = !message
    }

    _csrf() {
        return document.querySelector("meta[name='csrf-token']")?.content || ""
    }
}
